import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import { UserSoloStats } from 'src/api/types/users'
import {
  altColorDarkText,
  altColorLighter,
  headingFont,
  mainColor,
  mainColorDarker,
  mainColorLighter,
} from 'src/ui/theme/tokens'

export function soloLevelProgress(stats: UserSoloStats) {
  const level = Math.max(1, stats.soloLevel || 1)
  const xp = Math.max(0, stats.soloXp || 0)
  const floor = (level - 1) ** 2 * 100
  const ceiling = level ** 2 * 100
  return {
    level,
    xp,
    remaining: Math.max(0, ceiling - xp),
    progress: Math.min(100, Math.max(0, ((xp - floor) / (ceiling - floor)) * 100)),
  }
}

export default function SoloProgressStrip({
  stats,
  onOpen,
  compact = false,
}: {
  stats: UserSoloStats
  onOpen: () => void
  compact?: boolean
}) {
  const { level, remaining, progress } = soloLevelProgress(stats)
  const streak = stats.dailyStreak || 0
  const levelLabel = `Solo level ${level}, ${remaining} XP to level ${level + 1}. Open Solo Progress`
  if (compact) {
    return (
      <Box
        data-testid="solo-progress-strip"
        data-variant="compact"
        sx={{ display: 'flex', gap: 1, alignItems: 'stretch', mb: 1.5 }}
      >
        <ButtonBase
          onClick={onOpen}
          aria-label={levelLabel}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 44,
            pl: 1,
            pr: 1.25,
            gap: 1,
            borderRadius: '14px',
            border: '1px solid #b6e0ec',
            background: mainColorLighter,
            textAlign: 'left',
            '@media (hover: hover) and (pointer: fine)': {
              '&:hover': { filter: 'brightness(0.98)' },
            },
          }}
        >
          <Box
            aria-hidden
            data-testid="solo-level-number"
            data-rt-display
            sx={{
              display: 'grid',
              placeItems: 'center',
              minWidth: 32,
              height: 32,
              px: 0.5,
              flexShrink: 0,
              borderRadius: '10px',
              background: mainColor,
              color: mainColorDarker,
              fontFamily: headingFont,
              fontSize: 20,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {level}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LinearProgress
              aria-label="Progress to next solo level"
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: '99px',
                background: '#c3e4ed',
                '& .MuiLinearProgress-bar': {
                  background: mainColorDarker,
                  borderRadius: '99px',
                },
              }}
            />
            <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.3 }}>
              {remaining.toLocaleString()} XP to level {level + 1}
            </Typography>
          </Box>
        </ButtonBase>
        <ButtonBase
          onClick={onOpen}
          aria-label={`${streak}-day streak. Open Solo Progress`}
          sx={{
            flexDirection: 'column',
            gap: 0.25,
            width: 48,
            minHeight: 44,
            flexShrink: 0,
            border: '1px solid #f2d5b8',
            boxShadow: '0 3px 0 #e8bb99',
            borderRadius: '14px',
            background: altColorLighter,
            color: altColorDarkText,
            '@media (hover: hover) and (pointer: fine)': {
              '&:hover': { filter: 'brightness(0.98)' },
            },
          }}
        >
          <LocalFireDepartmentRoundedIcon sx={{ fontSize: 18 }} />
          <Typography
            data-testid="solo-streak-number"
            data-rt-display
            sx={{
              fontFamily: headingFont,
              fontSize: 12,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {streak}
          </Typography>
        </ButtonBase>
      </Box>
    )
  }
  const ticket = {
    borderRadius: '22px',
    px: { xs: 1.5, sm: 1.75 },
    py: { xs: 1, sm: 1.25 },
    boxShadow: '0 3px 0 #d4e3ed',
    textAlign: 'left',
    minWidth: 0,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': { filter: 'brightness(0.98)' },
    },
  }
  return (
    <Box
      data-testid="solo-progress-strip"
      data-variant="daily"
      sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', mb: 1.5 }}
    >
      <ButtonBase
        onClick={onOpen}
        aria-label={levelLabel}
        sx={{
          ...ticket,
          background: mainColorLighter,
          border: '1px solid #b6e0ec',
          flex: 1,
          justifyContent: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          aria-hidden
          data-rt-display
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: { xs: 36, sm: 42 },
            height: { xs: 38, sm: 42 },
            flexShrink: 0,
            borderRadius: '14px',
            background: mainColor,
            color: mainColorDarker,
            fontFamily: headingFont,
            fontSize: 24,
            fontWeight: 800,
            boxShadow: '0 3px 0 #81cee5',
          }}
        >
          {level}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize={{ xs: 13, sm: 15 }}>
            Solo level {level}
          </Typography>
          <LinearProgress
            aria-label="Progress to next solo level"
            variant="determinate"
            value={progress}
            sx={{
              my: 0.5,
              height: 6,
              maxWidth: 260,
              borderRadius: '99px',
              background: '#c3e4ed',
              '& .MuiLinearProgress-bar': {
                background: mainColorDarker,
                borderRadius: '99px',
              },
            }}
          />
          <Typography fontSize={{ xs: 10, sm: 12 }} color="text.secondary">
            {remaining.toLocaleString()} XP to level {level + 1}
          </Typography>
        </Box>
      </ButtonBase>
      <ButtonBase
        onClick={onOpen}
        aria-label={`${streak}-day streak. Open Solo Progress`}
        sx={{
          ...ticket,
          background: altColorLighter,
          border: '1px solid #f2d5b8',
          boxShadow: '0 3px 0 #e8bb99',
          color: altColorDarkText,
          gap: 0.5,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: 92, sm: 'auto' },
          px: { xs: 1, sm: 2.5 },
        }}
      >
        <LocalFireDepartmentRoundedIcon sx={{ fontSize: { xs: 20, sm: 26 } }} />
        <Box>
          <Typography
            fontSize={{ xs: 16, sm: 18 }}
            fontWeight={800}
            sx={{ whiteSpace: 'nowrap', lineHeight: 1, textAlign: 'center' }}
          >
            {streak}
            <Box
              component="span"
              sx={{ display: 'block', fontSize: { xs: 10, sm: 11 }, lineHeight: 1.3 }}
            >
              day streak
            </Box>
          </Typography>
          <Typography fontSize={11} sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.8 }}>
            Keep it going!
          </Typography>
        </Box>
      </ButtonBase>
    </Box>
  )
}
